import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { CalendarDays, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useChecklists, useComments, useAttachments, useDependencies, useAverageTaskTime, useTimeLogs, useCardProcedures, addCardMember, removeCardMember, useProcedureAttachments } from '../hooks/useKanbifyData'
import { useCompany } from '../hooks/useCompanyData'
import { useAuth } from '../contexts/AuthContext'
import { kanbify } from '../lib/kanbifyClient'
import { useAchievement } from '../contexts/AchievementContext'
import { playSound } from '../lib/sounds'
import { isDueOverdue } from '../lib/dueDate'
import { PROC_DAYS } from './ui/ProcDayPicker'
import { claimGeneralTask, claimProcedure, leaveProcedure, setStepProgress, startTimerWithValidation } from '../hooks/useDashboardV2'
import CardDetailView from './card-detail/CardDetailView'
import useCardAutoTimer from './card-detail/useCardAutoTimer'
import useCardCommentsAndUploads from './card-detail/useCardCommentsAndUploads'
import useCardCompletionFlow from './card-detail/useCardCompletionFlow'
import {
    STEP_KPI_ENABLED,
    deriveCardDetailExternalState,
    groupTimeLogs,
    isImageAttachment,
} from './card-detail/cardDetailUtils'

import { getCardKpiFields, stripKpiNoise } from '../lib/kpi'
import { normalizeCompletionPayload } from '../lib/cardCompletion'

import CompletionKpiModal from './CompletionKpiModal'

export default function CardDetailModal({
    card, allCards = [], onClose, onUpdate, onDelete, onRefreshCards, isAdmin: isAdminProp, canCreate: canCreateProp, procedures = [], readOnly = false
}) {
    const { t } = useTranslation()
    const { user, profile } = useAuth()
    const { showAchievement } = useAchievement()

    // Spectator / read-only mode (CEO "intri in corpul angajatului, dar doar vezi"):
    // neutralize every capability flag at the source so ALL downstream write
    // controls — admin actions, contribution, claim, timer/status — disappear.
    const isAdmin = isAdminProp && !readOnly
    const canCreate = canCreateProp && !readOnly

    // Pool cards are writable only after the current user has claimed them.
    // Read live claim state because the prop can lag after dashboard RPCs.
    const [liveClaim, setLiveClaim] = useState(null)
    const [timerOverride, setTimerOverride] = useState({ cardId: null, value: undefined })
    const [completionOverride, setCompletionOverride] = useState({ cardId: null, value: undefined })
    const [memberOverride, setMemberOverride] = useState({ cardId: null, members: null })
    const effectiveTimerStartedAt = timerOverride.cardId === card?.id ? timerOverride.value : card?.timer_started_at
    const effectiveIsCompleted = completionOverride.cardId === card?.id ? completionOverride.value : Boolean(card?.is_completed)

    useEffect(() => {
        setTimerOverride({ cardId: null, value: undefined })
        setCompletionOverride({ cardId: null, value: undefined })
        setMemberOverride({ cardId: null, members: null })
    }, [card?.id])

    useEffect(() => {
        if (!card?.id) return undefined
        const handleDashboardCardState = (event) => {
            const next = deriveCardDetailExternalState({
                currentCardId: card.id,
                eventDetail: event.detail,
            })
            if (!next.applies) return
            if (Object.prototype.hasOwnProperty.call(next, 'completionValue')) {
                setCompletionOverride({ cardId: card.id, value: next.completionValue })
            }
            if (Object.prototype.hasOwnProperty.call(next, 'timerValue')) {
                setTimerOverride({ cardId: card.id, value: next.timerValue })
            }
        }
        window.addEventListener('kanbify:dashboard-card-state', handleDashboardCardState)
        return () => window.removeEventListener('kanbify:dashboard-card-state', handleDashboardCardState)
    }, [card?.id])

    useEffect(() => {
        if (!card?.id || !card?.is_general_pool) return
        let cancelled = false
        kanbify.from('cards').select('claimed_by, card_members(user_id, claimed_at)').eq('id', card.id).maybeSingle()
            .then(({ data }) => {
                if (cancelled || !data) return
                const myClaim = (data.card_members || []).find(m => m.user_id === user?.id && m.claimed_at)
                setLiveClaim({ id: card.id, claimed_by: data.claimed_by ?? null, my_claimed_at: myClaim?.claimed_at || null })
            })
        return () => { cancelled = true }
    }, [card?.id, card?.is_general_pool, user?.id])
    // Drop the optimistic procedure-join override whenever the card changes.
    useEffect(() => { setProcedureJoinOverride(null) }, [card?.id])
    const effectiveClaimedBy = (liveClaim && liveClaim.id === card?.id) ? liveClaim.claimed_by : card?.claimed_by
    const propAssignedToMe = (card?.card_members || []).some(m => (m.user_id || m.profiles?.id) === user?.id)
    const propClaimedByMe = (card?.card_members || []).some(m => (m.user_id || m.profiles?.id) === user?.id && m.claimed_at)
    const poolClaimedByMe = Boolean(card?.is_general_pool && user?.id && (effectiveClaimedBy === user.id || (liveClaim?.id === card?.id && liveClaim.my_claimed_at) || propClaimedByMe))
    const canClaimPoolTask = Boolean(card?.is_general_pool && user?.id && !poolClaimedByMe && !effectiveIsCompleted && (!effectiveClaimedBy || propAssignedToMe)) && !readOnly
    const canWorkOnCard = Boolean(!card?.is_general_pool || poolClaimedByMe) && !readOnly
    // Contributing (files + comment images) is open to anyone who can open the
    // card — including unclaimed or other-claimed pool tasks — so colleagues can
    // help before a task is picked up (Teo, 2026-05-22). "Working" (timer/status)
    // stays claimer-only via canWorkOnCard. Backend is company-scoped already, so
    // this is purely a UI gate relaxation.
    const canContribute = Boolean(user?.id) && !readOnly
    const { members: companyMembers } = useCompany(profile?.company_id)
    const {
        checklists, fetch: fetchChecklists,
        addChecklist, removeChecklist, addItem, toggleItem, toggleShared, updateSharedMembers, updateItem, deleteItem
    } = useChecklists(card?.id)
    const { comments, commentReactions, fetch: fetchComments, add: addComment, update: updateComment, remove: removeComment, toggleCommentReaction } = useComments(card?.id)
    const [editingCommentId, setEditingCommentId] = useState(null)
    const [editingCommentText, setEditingCommentText] = useState('')
    const [replyingTo, setReplyingTo] = useState(null)
    const [replyText, setReplyText] = useState('')
    const [replySubmitting, setReplySubmitting] = useState(false)
    const replyImageInputRef = useRef(null)
    const { attachments, fetch: fetchAttachments, uploadAttachment, uploadAttachments, linkAttachmentsToComment, moveAttachment, removeAttachment } = useAttachments(card?.id)
    const { dependencies, fetch: fetchDependencies, addDependency, removeDependency } = useDependencies(card?.id)
    const { cardProcedures, link: linkProcedure } = useCardProcedures(card?.id)
    // Read-only: files attached to the procedure this card was spawned from, so
    // the worker doing the daily card can open the reference docs (no copy).
    const linkedProcedureId = cardProcedures[0]?.procedure_id
    const { attachments: procedureFiles } = useProcedureAttachments(linkedProcedureId)
    const { averageMinutes, loading: loadingAverage } = useAverageTaskTime(card?.id)
    const { logs: timeLogs, addTimeLog } = useTimeLogs(card?.id)

    const groupedTimeLogs = useMemo(() => groupTimeLogs(timeLogs), [timeLogs])

    const emitDashboardCardState = useCallback((type, patch = {}) => {
        if (!card?.id) return
        window.dispatchEvent(new CustomEvent('kanbify:dashboard-card-state', {
            detail: {
                type,
                cardId: card.id,
                patch: {
                    ...card,
                    ...patch,
                },
            },
        }))
    }, [card])

    const [editingTitle, setEditingTitle] = useState(false)
    const [title, setTitle] = useState('')
    const [editingDesc, setEditingDesc] = useState(false)
    const [desc, setDesc] = useState('')
    // Refs reflect editing state without forcing the resync effect below to
    // re-run when the flag toggles. The 15s realtime poll (shim) produces a
    // new `card` ref every tick — without these guards the resync effect
    // wipes the user's unsaved input every 15s (same class as the dashboard
    // scroll-reset fix 2026-05-19).
    const editingTitleRef = useRef(false)
    const editingDescRef = useRef(false)
    useEffect(() => { editingTitleRef.current = editingTitle }, [editingTitle])
    useEffect(() => { editingDescRef.current = editingDesc }, [editingDesc])
    const [newItemTexts, setNewItemTexts] = useState({})
    const [editingItemId, setEditingItemId] = useState(null)
    const [editingItemText, setEditingItemText] = useState('')
    const [expandedSharedItem, setExpandedSharedItem] = useState(null)
    const [sharedItemSearch, setSharedItemSearch] = useState('')
    const [commentText, setCommentText] = useState('')
    const [addingChecklist, setAddingChecklist] = useState(false)
    const [newChecklistName, setNewChecklistName] = useState(() => t('card.checklist.defaultName'))
    const [activeTab, setActiveTab] = useState('checklist') // checklist | members | comments | attachments
    const [commentError, setCommentError] = useState('')
    const [submittingComment, setSubmittingComment] = useState(false)
    const [claimingPoolTask, setClaimingPoolTask] = useState(false)
    const [cardActionPending, setCardActionPending] = useState(null)
    // Optimistic override for procedure self-join (claim_procedure / leave_procedure):
    // null = follow card data; true = joined; false = left. Keyed to card via reset on open.
    const [procedureJoinOverride, setProcedureJoinOverride] = useState(null)
    const [joiningProcedure, setJoiningProcedure] = useState(false)
    const [stepBusyId, setStepBusyId] = useState(null)        // procedure step item mid-update
    const [qtyPopoverId, setQtyPopoverId] = useState(null)    // item id whose "de mai multe ori" popover is open
    const [qtyDraft, setQtyDraft] = useState('')
    const [updateError, setUpdateError] = useState('')
    const [addingMember, setAddingMember] = useState(false)
    const [uploadingFile, setUploadingFile] = useState(false)
    const [addingDependency, setAddingDependency] = useState(false)
    const [dependType, setDependType] = useState('blocks')
    const [dependTargetId, setDependTargetId] = useState('')
    const [linkingProcedure, setLinkingProcedure] = useState('')
    const [linkingSchedule, setLinkingSchedule] = useState({ frequency: 'daily', repeat_interval: 1, repeat_days: [] })
    const [epics, setEpics] = useState([])
    const [fieldStatus, setFieldStatus] = useState({})
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [confirmRemoveAttachment, setConfirmRemoveAttachment] = useState(null)
    const [lightboxIndex, setLightboxIndex] = useState(null)
    const [previewFile, setPreviewFile] = useState(null)
    const [lightboxImages, setLightboxImages] = useState(null)
    const [confirmRemoveMember, setConfirmRemoveMember] = useState(null)
    const [confirmDeleteChecklist, setConfirmDeleteChecklist] = useState(null)
    const [showCompletionModal, setShowCompletionModal] = useState(false)
    const [completionModalSaving, setCompletionModalSaving] = useState(false)
    const [sharedCardPicker, setSharedCardPicker] = useState(false)
    const [sharedItemPicker, setSharedItemPicker] = useState(null) // { checklistId, itemId } or null
    const [selectedKpiUserId, setSelectedKpiUserId] = useState(null)
    const [addingChecklistLoading, setAddingChecklistLoading] = useState(false)
    const [addingItemLoading, setAddingItemLoading] = useState(null)
    const [linkingProcLoading, setLinkingProcLoading] = useState(false)
    const [addingDepLoading, setAddingDepLoading] = useState(false)
    const [addingMemberLoading, setAddingMemberLoading] = useState(false)
    const [removingMemberLoading, setRemovingMemberLoading] = useState(false)
    const fileInputRef = useRef(null)
    const commentImageInputRef = useRef(null)

    const openLightbox = useCallback((images, index = 0) => {
        const list = Array.isArray(images) ? images.filter(isImageAttachment) : []
        if (!list.length) return
        setLightboxImages(list)
        setLightboxIndex(Math.min(Math.max(index, 0), list.length - 1))
    }, [])

    const closeLightbox = useCallback(() => {
        setLightboxIndex(null)
        setLightboxImages(null)
    }, [])

    // Close on Escape (only when no sub-dialog is open)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (previewFile || lightboxIndex !== null || confirmDelete || confirmRemoveAttachment || confirmRemoveMember || confirmDeleteChecklist || showCompletionModal) return
                onClose()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [onClose, previewFile, lightboxIndex, confirmDelete, confirmRemoveAttachment, confirmRemoveMember, confirmDeleteChecklist, showCompletionModal])

    const { activeTimer, persistActiveTimer } = useCardAutoTimer({
        t,
        card,
        userId: user?.id,
        readOnly,
        canWorkOnCard,
        effectiveClaimedBy,
        effectiveTimerStartedAt,
        effectiveIsCompleted,
        setTimerOverride,
        addTimeLog,
        onUpdate,
        showAchievement,
        setUpdateError,
        emitDashboardCardState,
    })

    const handleClaimPoolTask = useCallback(async () => {
        if (!card?.id || !user?.id || claimingPoolTask) return
        setClaimingPoolTask(true)
        setCardActionPending('claim')
        setUpdateError('')
        try {
            const res = await claimGeneralTask(card.id)
            const alreadyMine = !res.success && res.claimed_by === user.id
            if (!res.success && !alreadyMine) {
                showAchievement({ type: 'error', message: res.error || t('dashboard.toast.claimFailed') })
                return
            }
            const claimedAt = res.claimed_at || new Date().toISOString()
            setLiveClaim({ id: card.id, claimed_by: res.claimed_by || user.id, my_claimed_at: claimedAt })
            emitDashboardCardState('claimed', {
                claimed_by: res.claimed_by || user.id,
                claimed_at: claimedAt,
                _claimedUserIds: [user.id],
                timer_started_at: null,
            })
            showAchievement({ type: 'success', message: t('dashboard.toast.claimSuccess') })
            playSound('check')
            onRefreshCards?.()
            window.dispatchEvent(new CustomEvent('kanbify:dashboard-refetch'))
        } finally {
            setClaimingPoolTask(false)
            setCardActionPending(null)
        }
    }, [card?.id, claimingPoolTask, emitDashboardCardState, onRefreshCards, showAchievement, t, user?.id])

    const handleClaimProcedure = useCallback(async () => {
        if (!card?.id || !user?.id || joiningProcedure) return
        setJoiningProcedure(true)
        setUpdateError('')
        try {
            const res = await claimProcedure(card.id)
            if (!res.success) {
                showAchievement({ type: 'error', message: res.error || t('dashboard.toast.claimFailed') })
                return
            }
            setProcedureJoinOverride(true)
            showAchievement({ type: 'success', message: t('card.procedure.claimedToHome') })
            playSound('check')
            onRefreshCards?.()
            window.dispatchEvent(new CustomEvent('kanbify:dashboard-refetch'))
        } finally {
            setJoiningProcedure(false)
        }
    }, [card?.id, joiningProcedure, onRefreshCards, showAchievement, t, user?.id])

    const handleLeaveProcedure = useCallback(async () => {
        if (!card?.id || !user?.id || joiningProcedure) return
        setJoiningProcedure(true)
        setUpdateError('')
        try {
            const res = await leaveProcedure(card.id)
            if (!res.success) {
                showAchievement({ type: 'error', message: res.error || t('dashboard.toast.claimFailed') })
                return
            }
            setProcedureJoinOverride(false)
            showAchievement({ type: 'success', message: t('card.procedure.leftHome') })
            onRefreshCards?.()
            window.dispatchEvent(new CustomEvent('kanbify:dashboard-refetch'))
        } finally {
            setJoiningProcedure(false)
        }
    }, [card?.id, joiningProcedure, onRefreshCards, showAchievement, t, user?.id])

    const handleStartFromDetail = useCallback(async () => {
        if (!card?.id || !canWorkOnCard || effectiveTimerStartedAt || effectiveIsCompleted || cardActionPending) return
        setUpdateError('')
        setCardActionPending('start')
        try {
            const res = await startTimerWithValidation(card.id)
            if (!res.success) {
                const error = res.error || t('card.timer.couldNotStart')
                setUpdateError(error)
                showAchievement({ type: 'error', message: error })
                return
            }
            const startedAt = new Date().toISOString()
            setTimerOverride({ cardId: card.id, value: startedAt })
            emitDashboardCardState('started', { timer_started_at: startedAt, is_completed: false })
            showAchievement({ type: 'success', message: t('dashboard.toast.timerStarted') })
            playSound('check')
            onRefreshCards?.()
            window.dispatchEvent(new CustomEvent('kanbify:dashboard-refetch'))
        } finally {
            setCardActionPending(null)
        }
    }, [card?.id, canWorkOnCard, cardActionPending, effectiveTimerStartedAt, effectiveIsCompleted, emitDashboardCardState, onRefreshCards, showAchievement, t])

    const cardId = card?.id
    const cardTitle = card?.title || ''
    // KPI v4 cleanup (Teo 2026-05-26): strip marker + linii KPI legacy.
    const cardDescription = stripKpiNoise(card?.description)

    useEffect(() => {
        if (!cardId) return
        // Skip overwriting title/desc while user is mid-edit. Poll/refetch can
        // refresh the card object, but typing should never be wiped.
        if (!editingTitleRef.current) setTitle(cardTitle)
        if (!editingDescRef.current) setDesc(cardDescription)
    }, [cardId, cardTitle, cardDescription])

    useEffect(() => {
        if (!cardId) return
        setUpdateError('')
        setCardActionPending(null)
        setFieldStatus({})
        fetchChecklists()
        fetchComments()
        fetchAttachments()
        fetchDependencies()
    }, [cardId, fetchAttachments, fetchChecklists, fetchComments, fetchDependencies])

    const fetchEpics = useCallback(async () => {
        if (!card?.list_id) {
            setEpics([])
            return
        }
        const { data } = await kanbify.from('cards')
            .select('id, title')
            .eq('issue_type', 'epic')
            .neq('id', card.id)
        setEpics(data || [])
    }, [card?.id, card?.list_id])

    useEffect(() => {
        fetchEpics()
    }, [fetchEpics])

    // KPI v4: prefer per-card snapshot (cards.kpi_fields w/ points) over the
    // legacy description parse — modal shows pt/u + total preview when set.
    const completionFields = useMemo(() => getCardKpiFields(card), [card])
    const normalizedCompletionPayload = useMemo(() => normalizeCompletionPayload(card?.completion_payload), [card?.completion_payload])
    const myCompletionKpis = user?.id ? (normalizedCompletionPayload.by_user?.[user.id]?.values || {}) : {}
    const procedureIndexMap = useMemo(() => new Map(
        procedures.map((procedure, index) => [procedure.id, index]),
    ), [procedures])

    const isOverdue = isDueOverdue(card?.due_date, effectiveIsCompleted)
    const effectiveCardMembers = useMemo(() => (
        memberOverride.cardId === card?.id && Array.isArray(memberOverride.members)
            ? memberOverride.members
            : (card?.card_members || [])
    ), [card?.card_members, card?.id, memberOverride])
    const members = useMemo(() => effectiveCardMembers.map(cm => cm.profiles).filter(Boolean), [effectiveCardMembers])
    // Procedure self-join ("Prelua o procedură chiar dacă nu sunt asignat"):
    // any company member can join a procedure card → it then shows in their Acasă.
    // Additive (non-exclusive), unlike the general-pool claim above.
    const isProcedureCard = cardProcedures.length > 0
    const isCardMemberRaw = Boolean(user?.id && effectiveCardMembers.some(cm => cm.user_id === user.id))
    const isCardMember = procedureJoinOverride === null ? isCardMemberRaw : procedureJoinOverride
    const canClaimProcedure = Boolean(isProcedureCard && user?.id && !isCardMember && !card?.is_general_pool && !effectiveIsCompleted) && !readOnly
    const canLeaveProcedure = Boolean(isProcedureCard && user?.id && isCardMember && !card?.is_general_pool && !isAdmin) && !readOnly
    const isCardCreator = Boolean(user?.id && card?.created_by === user.id)
    const canManageCardMembers = Boolean(!readOnly && (isAdmin || canCreate || isCardCreator || isCardMember || poolClaimedByMe))
    const canDeleteCard = Boolean(!readOnly && (isAdmin || canCreate || isCardCreator))
    // Description was admin-only — too restrictive: an employee creating a
    // "general task" couldn't edit its own description. Allow edit if user
    // is admin, on the card members, or the pool claimant.
    const canEditDescription = Boolean(
        isAdmin
        || (user?.id && effectiveCardMembers.some(cm => cm.user_id === user.id))
        || poolClaimedByMe,
    )
    const labels = useMemo(() => card?.card_labels?.map(cl => cl.labels).filter(Boolean) || [], [card?.card_labels])

    const displayCompletionKpis = useMemo(() => {
        const byUser = normalizedCompletionPayload.by_user || {}
        const targetId = selectedKpiUserId
            || card?.completion_submitted_by
            || Object.keys(byUser)[0]
            || null
        if (!targetId) return null
        return byUser[targetId]?.values || {}
    }, [normalizedCompletionPayload, card?.completion_submitted_by, selectedKpiUserId])

    const handleSaveTitle = async () => {
        if (title.trim() && title !== card.title) await onUpdate(card.id, { title: title.trim() })
        setEditingTitle(false)
    }

    const handleSaveDesc = async () => {
        await onUpdate(card.id, { description: desc.trim() || null })
        setEditingDesc(false)
    }

    const updateFieldStatus = (field, status, errorMessage = '') => {
        setFieldStatus((previous) => ({
            ...previous,
            [field]: { status, errorMessage },
        }))
    }

    const runFieldUpdate = async (field, updates) => {
        updateFieldStatus(field, 'saving')
        const result = await onUpdate(card.id, updates)

        if (result?.success === false) {
            updateFieldStatus(field, 'error', result.error || t('card.field.couldNotSave'))
            return result
        }

        updateFieldStatus(field, 'saved')
        window.setTimeout(() => {
            setFieldStatus((previous) => {
                if (previous[field]?.status !== 'saved') return previous
                return {
                    ...previous,
                    [field]: { status: 'idle', errorMessage: '' },
                }
            })
        }, 1400)

        return { success: true }
    }

    const renderFieldState = (field) => {
        const state = fieldStatus[field]
        if (!state || state.status === 'idle') return null

        const label = state.status === 'saving'
            ? t('card.field.savingShort')
            : state.status === 'saved'
                ? t('card.field.savedShort')
                : (state.errorMessage || t('card.field.errorGeneric'))

        return <div className={`card-field-status ${state.status}`}>{label}</div>
    }

    const handleToggleItem = async (checklistId, itemId) => {
        if (!user?.id) return
        const wasCompleted = await toggleItem(checklistId, itemId, user.id)
        if (wasCompleted) {
            playSound('check')
            // KPI v3: per-checklist-item points removed. Only card completion
            // (cards.point_value) and procedure_completions award points now.
        }
        onRefreshCards()
    }

    // Procedure step (KPI) progress. Each step carries point_value; completing
    // awards point_value × quantity to the user immediately (reversible).
    const stepDoneQty = useCallback((item) => {
        if (item.is_shared) {
            const mc = (item.completions || []).find(c => c.user_id === user?.id)
            return { done: !!mc, qty: Number(mc?.quantity) || 1 }
        }
        return { done: !!item.is_completed, qty: Number(item.quantity) || 1 }
    }, [user?.id])

    const applyStepProgress = useCallback(async (item, done, qty) => {
        if (!user?.id || stepBusyId) return
        setStepBusyId(item.id)
        try {
            const res = await setStepProgress(item.id, done, qty)
            if (!res.success) { showAchievement({ type: 'error', message: res.error || t('card.field.errorGeneric') }); return }
            if (done) playSound('check')
            await fetchChecklists()
            onRefreshCards()
            window.dispatchEvent(new CustomEvent('kanbify:points-refresh'))
            window.dispatchEvent(new CustomEvent('kanbify:dashboard-refetch'))
        } finally { setStepBusyId(null); setQtyPopoverId(null) }
    }, [user?.id, stepBusyId, onRefreshCards, fetchChecklists, showAchievement, t])

    const handleStepToggle = (item) => {
        const { done } = stepDoneQty(item)
        applyStepProgress(item, !done, done ? 1 : 1) // toggling on starts at 1; popover sets a specific count
    }
    const handleStepQuantity = (item, n) => {
        const qty = Math.max(1, parseInt(n, 10) || 1)
        applyStepProgress(item, true, qty)
    }

    const handleAddItem = async (checklistId) => {
        const text = newItemTexts[checklistId]
        if (!text?.trim() || addingItemLoading === checklistId) return
        setAddingItemLoading(checklistId)
        await addItem(checklistId, text.trim())
        setNewItemTexts(p => ({ ...p, [checklistId]: '' }))
        setAddingItemLoading(null)
        onRefreshCards()
    }

    const {
        handleAddComment,
        handleCommentImageUpload,
        handleAddReply,
        handleToggleCommentReaction,
    } = useCardCommentsAndUploads({
        t,
        card,
        user,
        profile,
        members,
        companyMembers,
        commentText,
        setCommentText,
        submittingComment,
        setSubmittingComment,
        setCommentError,
        addComment,
        canContribute,
        uploadAttachment,
        linkAttachmentsToComment,
        fetchComments,
        fetchAttachments,
        setUploadingFile,
        setActiveTab,
        showAchievement,
        replyText,
        setReplyText,
        setReplyingTo,
        replySubmitting,
        setReplySubmitting,
        toggleCommentReaction,
    })

    const {
        doneForCurrentUser,
        configLocked,
        finalizeCardCompletion,
        handleCompleteFromDetail,
    } = useCardCompletionFlow({
        t,
        card,
        user,
        profile,
        readOnly,
        effectiveIsCompleted,
        effectiveTimerStartedAt,
        completionOverride,
        setCompletionOverride,
        cardActionPending,
        setCardActionPending,
        setTimerOverride,
        setUpdateError,
        setShowCompletionModal,
        normalizedCompletionPayload,
        completionFields,
        cardProcedures,
        members,
        checklists,
        isProcedureCard,
        persistActiveTimer,
        onUpdate,
        onRefreshCards,
        emitDashboardCardState,
        showAchievement,
    })

    if (!card) return null

    const handleToggleCardShared = async () => {
        if (!card?.id) return
        if (card.is_shared) {
            // Turning OFF: clear per-user completions, but KEEP shared_member_ids
            // in DB so the list is remembered when the user re-activates later
            // (Teo 2026-05-26 — UX: no need to re-pick the same people every
            // time the toggle flips).
            await kanbify.from('card_completions').delete().eq('card_id', card.id)
            await onUpdate(card.id, { is_shared: false })
            onRefreshCards()
        } else {
            // Turning ON: if we remember members from a previous activation,
            // re-use them directly. Otherwise open the picker for first-time
            // setup.
            const remembered = Array.isArray(card.shared_member_ids)
                ? card.shared_member_ids.filter(Boolean)
                : []
            if (remembered.length > 0) {
                await onUpdate(card.id, { is_shared: true, is_completed: false })
                onRefreshCards()
            } else {
                setSharedCardPicker(true)
            }
        }
    }

    const handleConfirmCardShared = async (memberIds) => {
        if (!card?.id) return
        setSharedCardPicker(false)
        await onUpdate(card.id, { is_shared: true, is_completed: false, shared_member_ids: memberIds })
        onRefreshCards()
    }

    const handleUpdateCardSharedMembers = async (memberIds) => {
        if (!card?.id) return
        setSharedCardPicker(false)
        await onUpdate(card.id, { shared_member_ids: memberIds })
        // Remove completions from members that were removed from shared list
        const removed = (card.shared_member_ids || []).filter(id => !memberIds.includes(id))
        for (const uid of removed) {
            await kanbify.from('card_completions').delete().eq('card_id', card.id).eq('user_id', uid)
        }
        onRefreshCards()
    }

    const handleAddChecklist = async () => {
        if (!newChecklistName.trim() || addingChecklistLoading) return
        setAddingChecklistLoading(true)
        await addChecklist(newChecklistName.trim() || t('card.checklist.defaultName'))
        setAddingChecklistLoading(false)
        setAddingChecklist(false)
        setNewChecklistName(t('card.checklist.defaultName'))
        onRefreshCards()
    }

    const getProcedureChecklistStats = (procedureLink) => {
        const linkedChecklist = checklists.find((checklist) => checklist.id === procedureLink?.checklist_id)
        const linkedItems = linkedChecklist?.checklist_items || []
        const total = linkedItems.length
        const currentUserId = user?.id
        // For shared items, check per-user completion; for non-shared, check global
        const done = linkedItems.filter((item) => {
            if (item.is_shared && currentUserId) {
                return (item.completions || []).some((c) => c.user_id === currentUserId)
            }
            return item.is_completed
        }).length
        const progress = total > 0 ? Math.round((done / total) * 100) : 0

        return {
            linkedChecklist,
            total,
            done,
            progress,
            isComplete: total > 0 && done === total,
        }
    }

    const handleAddMember = async (userId) => {
        if (!userId || !card?.id || addingMemberLoading) return
        const memberProfile = (companyMembers || []).find(m => m?.id === userId)
        const previousMembers = effectiveCardMembers
        if (memberProfile && !previousMembers.some(cm => cm.user_id === userId)) {
            setMemberOverride({
                cardId: card.id,
                members: [...previousMembers, { card_id: card.id, user_id: userId, profiles: memberProfile }],
            })
        }
        setAddingMemberLoading(true)
        const { error } = isAdmin || canCreate
            ? await kanbify.rpc('admin_add_card_member', { p_card_id: card.id, p_user_id: userId })
            : await addCardMember(card.id, userId, {
                cardTitle: card.title,
                senderName: profile?.full_name || user?.email,
                actorId: user?.id,
            })
        setAddingMemberLoading(false)
        if (!error) {
            if ((isAdmin || canCreate) && card.is_general_pool && !effectiveClaimedBy) {
                setLiveClaim({ id: card.id, claimed_by: userId })
            }
            setAddingMember(false)
            onRefreshCards()
        } else {
            setMemberOverride({ cardId: card.id, members: previousMembers })
            showAchievement({ type: 'error', message: error.message || 'Nu am putut adăuga membrul.' })
        }
    }

    const handleRemoveMember = async (userId) => {
        if (!userId || !card?.id || removingMemberLoading) return
        const previousMembers = effectiveCardMembers
        setMemberOverride({
            cardId: card.id,
            members: previousMembers.filter(cm => cm.user_id !== userId),
        })
        setRemovingMemberLoading(true)
        const { data, error } = isAdmin || canCreate
            ? await kanbify.rpc('admin_remove_card_member', { p_card_id: card.id, p_user_id: userId })
            : await removeCardMember(card.id, userId)
        setRemovingMemberLoading(false)
        if (!error) {
            if (data?.removed_claim || effectiveClaimedBy === userId) {
                setLiveClaim({ id: card.id, claimed_by: null })
                setTimerOverride({ cardId: card.id, value: null })
            }
            onRefreshCards()
        }
        else {
            setMemberOverride({ cardId: card.id, members: previousMembers })
            showAchievement({ type: 'error', message: error.message || 'Nu am putut scoate membrul.' })
        }
        setConfirmRemoveMember(null)
    }

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || [])
        if (!files.length || !user?.id) return
        if (!canContribute) {
            showAchievement({ type: 'error', message: t('card.attachments.claimFirst') })
            if (fileInputRef.current) fileInputRef.current.value = ''
            return
        }

        setUploadingFile(true)
        const { uploaded, errors } = await uploadAttachments(files, user.id)
        if (uploaded > 0) {
            playSound('check')
            showAchievement({ type: 'success', message: t('card.attachments.uploadedMulti', { count: uploaded }) })
        }
        if (errors.length) {
            showAchievement({
                type: 'error',
                message: uploaded > 0
                    ? t('card.attachments.partialError', { ok: uploaded, fail: errors.length })
                    : t('card.attachments.uploadError', { error: errors[0].error }),
            })
        }
        setUploadingFile(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleAddDependencyLink = async () => {
        if (!dependTargetId || addingDepLoading) return
        setAddingDepLoading(true)
        await addDependency(dependType, dependTargetId)
        setAddingDepLoading(false)
        setAddingDependency(false)
        setDependTargetId('')
    }

    const availableMembers = (companyMembers || []).filter(
        m => m?.is_active !== false && !members.some(cm => cm?.id === m?.id)
    )

    // Total checklist progress (per current user for shared items)
    const allItems = checklists.flatMap(cl => cl.checklist_items || [])
    const totalItems = allItems.length
    const doneItems = allItems.filter(i =>
        i.is_shared
            ? (i.completions || []).some(c => c.user_id === user?.id)
            : i.is_completed
    ).length
    const progressPct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0
    const procedureByChecklistId = cardProcedures.reduce((acc, link) => {
        if (link.checklist_id) acc[link.checklist_id] = link.procedure
        return acc
    }, {})
    const procedureChecklists = checklists.filter((checklist) => Boolean(procedureByChecklistId[checklist.id]))
    const manualChecklists = checklists.filter((checklist) => !procedureByChecklistId[checklist.id])
    return (
        <CardDetailView
            {...{
                t, card, readOnly, onClose, updateError, isOverdue, effectiveIsCompleted, labels, editingTitle, title, setTitle, handleSaveTitle, setEditingTitle, cardDescription, editingDesc, setEditingDesc, desc, setDesc, handleSaveDesc, canEditDescription, canClaimPoolTask, handleClaimPoolTask, claimingPoolTask, poolClaimedByMe, canClaimProcedure, handleClaimProcedure, joiningProcedure, canLeaveProcedure, handleLeaveProcedure, activeTab, setActiveTab, totalItems, doneItems, attachments, dependencies, comments, progressPct, procedureChecklists, manualChecklists, checklists, addingChecklist, setAddingChecklist, newChecklistName, setNewChecklistName, handleAddChecklist, addingChecklistLoading, procedureByChecklistId, user, canCreate, canContribute, isAdmin, canManageCardMembers, canDeleteCard, members, companyMembers, editingItemId, editingItemText, setEditingItemId, setEditingItemText, updateItem, onRefreshCards, handleToggleItem, stepDoneQty, stepBusyId, qtyPopoverId, qtyDraft, setQtyDraft, setQtyPopoverId, handleStepToggle, handleStepQuantity, expandedSharedItem, setExpandedSharedItem, sharedItemSearch, setSharedItemSearch, sharedItemPicker, setSharedItemPicker, toggleShared, updateSharedMembers, deleteItem, newItemTexts, setNewItemTexts, handleAddItem, addingItemLoading, setConfirmDeleteChecklist, fileInputRef, handleFileUpload, uploadingFile, moveAttachment, setConfirmRemoveAttachment, procedureFiles, openLightbox, setPreviewFile, addingDependency, setAddingDependency, dependType, setDependType, dependTargetId, setDependTargetId, allCards, addingDepLoading, handleAddDependencyLink, removeDependency, profile, commentReactions, commentImageInputRef, handleCommentImageUpload, commentText, setCommentText, submittingComment, handleAddComment, commentError, editingCommentId, editingCommentText, setEditingCommentId, setEditingCommentText, updateComment, removeComment, showAchievement, handleToggleCommentReaction, replyingTo, setReplyingTo, replyText, setReplyText, replySubmitting, replyImageInputRef, handleAddReply, cardProcedures, procedures, linkingProcedure, setLinkingProcedure, linkingSchedule, setLinkingSchedule, linkingProcLoading, setLinkingProcLoading, linkProcedure, fetchChecklists, getProcedureChecklistStats, fieldStatus, renderFieldState, runFieldUpdate, configLocked, epics, completionFields, effectiveTimerStartedAt, doneForCurrentUser, procedureIndexMap, timeLogs, groupedTimeLogs, activeTimer, loadingAverage, averageMinutes, availableMembers, addingMember, setAddingMember, handleAddMember, setConfirmRemoveMember, canWorkOnCard, cardActionPending, handleStartFromDetail, handleCompleteFromDetail, normalizedCompletionPayload, selectedKpiUserId, setSelectedKpiUserId, displayCompletionKpis, handleToggleCardShared, sharedCardPicker, setSharedCardPicker, handleConfirmCardShared, handleUpdateCardSharedMembers, setConfirmDelete, confirmDelete, onDelete, confirmRemoveAttachment, removeAttachment, lightboxIndex, lightboxImages, setLightboxIndex, closeLightbox, previewFile, confirmRemoveMember, handleRemoveMember, confirmDeleteChecklist, removeChecklist, showCompletionModal, myCompletionKpis, completionModalSaving, setShowCompletionModal, setCompletionModalSaving, setUpdateError, finalizeCardCompletion
            }}
        />
    )
}
